import axios from 'axios';
import apiClient from './client';
import type {
  DaySettingsResponse,
  UpdateDaySettingsRequest,
  BranchDatesResponse,
  AddBranchDateRequest,
  AddBranchDateResponse,
  RemoveBranchDateResponse,
  TMPaymentsResponse,
  UpdatePaymentResponse,
  ContractorPaymentsResponse,
  BranchPaymentsResponse,
  PaymentIndicators,
  PaymentFileType,
  PaymentType,
  PaymentFile,
  Payment,
  PaymentNote,
  PublicInvoiceResponse,
} from '../types/payments.types';

// Fallback — относительный путь (см. комментарий в client.ts)
const API_URL = import.meta.env.VITE_API_URL || '/api';

// === НАСТРОЙКИ ДНЕЙ ===

export const getDaySettings = async (): Promise<DaySettingsResponse> => {
  const response = await apiClient.get<DaySettingsResponse>(
    '/payments/day-settings',
  );
  return response.data;
};

export const updateDaySettings = async (
  data: UpdateDaySettingsRequest,
): Promise<DaySettingsResponse> => {
  const response = await apiClient.put<DaySettingsResponse>(
    '/payments/day-settings',
    data,
  );
  return response.data;
};

export interface SyncBranchDatesRequest {
  year: number;
  month: number;
  activeDays: number[];
}

export interface SyncBranchDatesResponse {
  datesAdded: number;
  datesRemoved: number;
  datesKept: number;
  branchesProcessed: number;
}

export const syncBranchDates = async (
  data: SyncBranchDatesRequest,
): Promise<SyncBranchDatesResponse> => {
  const response = await apiClient.post<SyncBranchDatesResponse>(
    '/payments/sync-dates',
    data,
  );
  return response.data;
};

// === ДАТЫ ФИЛИАЛА ===

export const getBranchDates = async (
  branchId: string,
  year: number,
  month: number,
): Promise<BranchDatesResponse> => {
  const response = await apiClient.get<BranchDatesResponse>(
    `/payments/branch-dates/${branchId}`,
    { params: { year, month } },
  );
  return response.data;
};

export const addBranchDate = async (
  branchId: string,
  data: AddBranchDateRequest,
): Promise<AddBranchDateResponse> => {
  const response = await apiClient.post<AddBranchDateResponse>(
    `/payments/branch-dates/${branchId}`,
    data,
  );
  return response.data;
};

export const removeBranchDate = async (
  branchId: string,
  dateId: string,
): Promise<RemoveBranchDateResponse> => {
  const response = await apiClient.delete<RemoveBranchDateResponse>(
    `/payments/branch-dates/${branchId}/${dateId}`,
  );
  return response.data;
};

// === ПЛАТЕЖИ ТМ ===

export const getTMPayments = async (
  year: number,
  month: number,
): Promise<TMPaymentsResponse> => {
  const response = await apiClient.get<TMPaymentsResponse>(
    '/payments/tm/list',
    {
      params: { year, month },
    },
  );
  return response.data;
};

export const createTMPayment = async (
  monthlyPlanId: string,
  date: string,
  amount: number,
  type?: PaymentType,
): Promise<UpdatePaymentResponse> => {
  const response = await apiClient.post<UpdatePaymentResponse>(
    '/payments/tm/payment',
    {
      monthlyPlanId,
      date,
      amount,
      type,
    },
  );
  return response.data;
};

export const updateTMPayment = async (
  paymentId: string,
  amount: number,
): Promise<UpdatePaymentResponse> => {
  const response = await apiClient.put<UpdatePaymentResponse>(
    `/payments/tm/payment/${paymentId}`,
    { amount },
  );
  return response.data;
};

export const updateTMPaymentType = async (
  paymentId: string,
  type: PaymentType,
): Promise<Payment> => {
  const response = await apiClient.put<Payment>(
    `/payments/tm/payment/${paymentId}/type`,
    { type },
  );
  return response.data;
};

// === ДАТЫ ПОДРЯДЧИКА ===

export interface ContractorDatesResponse {
  contractorId: string;
  year: number;
  month: number;
  dates: Array<{
    date: string;
    dayOfWeek: number;
    isActive: boolean;
    canDelete: boolean;
  }>;
  calendar: Array<{
    date: string;
    dayOfWeek: number;
    isSelected: boolean;
    canSelect: boolean;
  }>;
}

export interface AddContractorDateResponse {
  date: string;
  dayOfWeek: number;
  isActive: boolean;
  canDelete: boolean;
  branchesAffected: number;
}

export interface RemoveContractorDateResponse {
  success: boolean;
  refundedAmount: number;
  paymentsDeleted: number;
  datesRemoved: number;
}

export const getContractorDates = async (
  year: number,
  month: number,
): Promise<ContractorDatesResponse> => {
  const response = await apiClient.get<ContractorDatesResponse>(
    '/payments/contractor/dates',
    {
      params: { year, month },
    },
  );
  return response.data;
};

export const addContractorDate = async (
  date: string,
): Promise<AddContractorDateResponse> => {
  const response = await apiClient.post<AddContractorDateResponse>(
    '/payments/contractor/dates',
    { date },
  );
  return response.data;
};

export const removeContractorDate = async (
  date: string,
): Promise<RemoveContractorDateResponse> => {
  const response = await apiClient.delete<RemoveContractorDateResponse>(
    `/payments/contractor/dates/${date}`,
  );
  return response.data;
};

export const deleteTMPayment = async (
  paymentId: string,
): Promise<{ success: boolean }> => {
  const response = await apiClient.delete<{ success: boolean }>(
    `/payments/tm/payment/${paymentId}`,
  );
  return response.data;
};

export const tmSubmitInvoice = async (paymentId: string): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/tm/submit-invoice/${paymentId}`,
  );
  return response.data;
};

export const tmForcePayInvoice = async (
  paymentId: string,
): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/tm/force-pay/${paymentId}`,
  );
  return response.data;
};

export const tmConfirmVerification = async (
  paymentId: string,
): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/tm/verify-confirm/${paymentId}`,
  );
  return response.data;
};

export const tmRejectVerification = async (
  paymentId: string,
): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/tm/verify-reject/${paymentId}`,
  );
  return response.data;
};

// === ПЛАТЕЖИ ПОДРЯДЧИКА ===

export const getContractorPayments = async (
  year: number,
  month: number,
): Promise<ContractorPaymentsResponse> => {
  const response = await apiClient.get<ContractorPaymentsResponse>(
    '/payments/contractor/list',
    {
      params: { year, month },
    },
  );
  return response.data;
};

export const createContractorPayment = async (
  monthlyPlanId: string,
  date: string,
  amount: number,
  type?: PaymentType,
): Promise<UpdatePaymentResponse> => {
  const response = await apiClient.post<UpdatePaymentResponse>(
    '/payments/contractor/payment',
    { monthlyPlanId, date, amount, type },
  );
  return response.data;
};

export const updateContractorPayment = async (
  paymentId: string,
  amount: number,
): Promise<UpdatePaymentResponse> => {
  const response = await apiClient.put<UpdatePaymentResponse>(
    `/payments/contractor/payment/${paymentId}`,
    { amount },
  );
  return response.data;
};

export const updateContractorPaymentType = async (
  paymentId: string,
  type: PaymentType,
): Promise<Payment> => {
  const response = await apiClient.put<Payment>(
    `/payments/contractor/payment/${paymentId}/type`,
    { type },
  );
  return response.data;
};

export const deleteContractorPayment = async (
  paymentId: string,
): Promise<{ success: boolean }> => {
  const response = await apiClient.delete<{ success: boolean }>(
    `/payments/contractor/payment/${paymentId}`,
  );
  return response.data;
};

export const submitInvoice = async (paymentId: string): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/contractor/submit-invoice/${paymentId}`,
  );
  return response.data;
};

export const forcePayInvoice = async (paymentId: string): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/contractor/force-pay/${paymentId}`,
  );
  return response.data;
};

export const contractorConfirmVerification = async (
  paymentId: string,
): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/contractor/verify-confirm/${paymentId}`,
  );
  return response.data;
};

export const contractorRejectVerification = async (
  paymentId: string,
): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/contractor/verify-reject/${paymentId}`,
  );
  return response.data;
};

// === ПЛАТЕЖИ ФИЛИАЛА ===

export const getBranchPayments = async (
  year: number,
  month: number,
): Promise<BranchPaymentsResponse> => {
  const response = await apiClient.get<BranchPaymentsResponse>(
    '/payments/branch/list',
    {
      params: { year, month },
    },
  );
  return response.data;
};

export const payInvoice = async (paymentId: string): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/branch/pay/${paymentId}`,
  );
  return response.data;
};

export const rejectInvoice = async (paymentId: string): Promise<Payment> => {
  const response = await apiClient.post<Payment>(
    `/payments/branch/reject/${paymentId}`,
  );
  return response.data;
};

// === ФАЙЛЫ ===

export const uploadPaymentFile = async (
  paymentId: string,
  type: PaymentFileType,
  file: File,
): Promise<PaymentFile> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);

  const response = await apiClient.post<PaymentFile>(
    `/payments/upload/${paymentId}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data;
};

export const uploadPaymentUrl = async (
  paymentId: string,
  type: PaymentFileType,
  url: string,
): Promise<PaymentFile> => {
  const response = await apiClient.post<PaymentFile>(
    `/payments/upload-url/${paymentId}`,
    { type, url },
  );
  return response.data;
};

export const getPaymentFileUrl = (fileId: string): string => {
  return `${API_URL}/payments/file/${fileId}`;
};

// === ПУБЛИЧНАЯ ССЫЛКА НА СЧЁТ ===

export const createPaymentPublicLink = async (
  paymentId: string,
): Promise<{ url: string }> => {
  const response = await apiClient.post<{ url: string }>(
    `/payments/${paymentId}/public-link`,
  );
  return response.data;
};

export const getPublicInvoice = async (
  token: string,
): Promise<PublicInvoiceResponse> => {
  // Отдельный axios-запрос намеренно не использует auth-интерсепторы:
  // страницу должен открыть человек без аккаунта в системе.
  const response = await axios.get<PublicInvoiceResponse>(
    `${API_URL}/payments/public/${encodeURIComponent(token)}`,
  );
  return response.data;
};

export const getPublicInvoiceFileUrl = (token: string): string => {
  return `${API_URL}/payments/public/${encodeURIComponent(token)}/file`;
};

export const getPublicCheckFileUrl = (token: string): string => {
  return `${API_URL}/payments/public/${encodeURIComponent(token)}/check`;
};

export const deletePaymentFile = async (fileId: string): Promise<void> => {
  await apiClient.delete(`/payments/file/${fileId}`);
};

export const getPaymentFiles = async (
  paymentId: string,
): Promise<{
  invoiceFile: PaymentFile | null;
  checkFile: PaymentFile | null;
}> => {
  const response = await apiClient.get<{
    invoiceFile: PaymentFile | null;
    checkFile: PaymentFile | null;
  }>(`/payments/${paymentId}/files`);
  return response.data;
};

// === ЗАМЕТКИ ===

export const getPaymentNotes = async (
  paymentId: string,
): Promise<PaymentNote[]> => {
  const response = await apiClient.get<PaymentNote[]>(
    `/payments/${paymentId}/notes`,
  );
  return response.data;
};

export const createPaymentNote = async (
  paymentId: string,
  text: string,
): Promise<PaymentNote> => {
  const response = await apiClient.post<PaymentNote>(
    `/payments/${paymentId}/notes`,
    { text },
  );
  return response.data;
};

export const deletePaymentNote = async (noteId: string): Promise<void> => {
  await apiClient.delete(`/payments/notes/${noteId}`);
};

// === ИНДИКАТОРЫ ===

export const getPaymentIndicators = async (): Promise<PaymentIndicators> => {
  const response = await apiClient.get<PaymentIndicators>(
    '/payments/indicators',
  );
  return response.data;
};

// === EXPORT API OBJECT ===

export const paymentsApi = {
  // Настройки дней
  getDaySettings,
  updateDaySettings,
  syncBranchDates,

  // Даты филиала
  getBranchDates,
  addBranchDate,
  removeBranchDate,

  // Даты подрядчика
  getContractorDates,
  addContractorDate,
  removeContractorDate,

  // Платежи ТМ
  getTMPayments,
  createTMPayment,
  updateTMPayment,
  updateTMPaymentType,
  deleteTMPayment,
  tmSubmitInvoice,
  tmForcePayInvoice,
  tmConfirmVerification,
  tmRejectVerification,

  // Платежи подрядчика
  getContractorPayments,
  createContractorPayment,
  updateContractorPayment,
  updateContractorPaymentType,
  deleteContractorPayment,
  submitInvoice,
  forcePayInvoice,
  contractorConfirmVerification,
  contractorRejectVerification,

  // Платежи филиала
  getBranchPayments,
  payInvoice,
  rejectInvoice,

  // Файлы
  uploadPaymentFile,
  uploadPaymentUrl,
  getPaymentFileUrl,
  createPaymentPublicLink,
  getPublicInvoice,
  getPublicInvoiceFileUrl,
  getPublicCheckFileUrl,
  deletePaymentFile,
  getPaymentFiles,

  // Заметки
  getPaymentNotes,
  createPaymentNote,
  deletePaymentNote,

  // Индикаторы
  getPaymentIndicators,
};

export default paymentsApi;
